/**
 * Store Pulse on SDK websites (Lovable, Bolt, v0).
 *
 * Store Pulse, live visitors, popups and chat run on the business's one code,
 * the commerce-agent tracker. Bootstrap hands the SDK its key, and the SDK
 * loads the tracker itself, so a website built with the SDK needs no second
 * script line. The tracker counts visits and page changes on its own; the SDK
 * reports what only it knows for sure (product views, add to cart, checkout
 * start), so the tracker's guessing from clicks and page text is switched off.
 *
 * A website that already has the one code keeps it exactly as it is, and the
 * SDK then reports nothing itself, so nothing is counted twice.
 */

export interface StorePulseConfig {
  key: string;
  scriptSrc: string;
  apiBase: string;
}

const SCRIPT_MARK = 'commerce-agent/script.js';
const QUEUE_MAX = 50;
const READY_WAIT_MS = 15_000;
const READY_POLL_MS = 300;

let reporting = false;
let queue: Array<[string, Record<string, unknown>]> = [];

const tracker = (): any => (typeof window !== 'undefined' ? (window as any).KhaimeCA : undefined);
const trackerReady = () => {
  const ca = tracker();
  return typeof ca?.track === 'function' && (typeof ca.isInitialized !== 'function' || ca.isInitialized());
};

function drain(): void {
  const ca = tracker();
  for (const [type, data] of queue) ca.track(type, data);
  queue = [];
}

function waitThenDrain(): void {
  const started = Date.now();
  const tick = () => {
    if (trackerReady()) return drain();
    if (Date.now() - started < READY_WAIT_MS) setTimeout(tick, READY_POLL_MS);
  };
  tick();
}

/**
 * Load the one code for this store. Returns true when the SDK reports events
 * (it loaded the tracker), false when the page already had the one code or
 * there is no page.
 */
export function startStorePulse(config: StorePulseConfig | null | undefined): boolean {
  if (!config?.key || typeof document === 'undefined') return false;
  const w = window as any;
  if (w.KhaimeCA?.version || document.querySelector(`script[src*="${SCRIPT_MARK}"]`)) return false;

  w.KhaimeCA = Object.assign(w.KhaimeCA || {}, {
    key: config.key,
    apiBase: config.apiBase,
    autoDetectCommerce: false,
  });
  const script = document.createElement('script');
  script.src = config.scriptSrc;
  script.async = true;
  script.setAttribute('data-key', config.key);
  script.onload = waitThenDrain;
  document.head.appendChild(script);
  reporting = true;
  return true;
}

/** Report a commerce event to Store Pulse; held until the tracker is ready. */
export function pulse(type: 'product_view' | 'add_to_cart' | 'checkout_start', data: Record<string, unknown>): void {
  if (!reporting) return;
  if (trackerReady()) {
    tracker().track(type, data);
    return;
  }
  queue.push([type, data]);
  if (queue.length > QUEUE_MAX) queue.shift();
}

/** For tests. */
export function resetStorePulse(): void {
  reporting = false;
  queue = [];
}
